import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BiChevronLeft } from 'react-icons/bi';
import { IoMdPersonAdd } from "react-icons/io";
import { IoSendSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';
import {
    Avatar,
    ContactNew,
    LoadingSpinner,
    MessageTemplates,
    Modal,
    Search,
    Skeleton,
    ViewerCustomerServiceOpen
} from '../../components';
import { IdUser } from '../../contexts/auth';
import { useChat } from '../../contexts/chat';
import {
    getTimeExpireSession,
    sendDocumentByLink,
    sendMessage,
    sendMsgTemplate,
    sendMsgTemplateParams
} from '../../service/serviceChat/chatService';
import { getFullClients } from '../../service/serviceChat/clientsService';
import {
    postCustomer,
    qtdCustomerServiceOpen
} from '../../service/serviceChat/customerService';
import {
    AddContact,
    Contacts,
    Container,
    Content,
    Revision,
    SelectMessage,
    Send
} from './styled';

const INITIAL_DISPLAY_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const MIN_SEARCH_LENGTH = 3;
const SKELETON_COUNT = 15;

export function NewChat(props) {
    const { close } = props;
    const { SelectOrderList, handleMyChats } = useChat();

    // State management
    const [openNewChat, setOpenNewChat] = useState(false);
    const [render, setRender] = useState(1);
    const [contacts, setContacts] = useState([]);
    const [contactSelected, setContactSelected] = useState(null);
    const [textSearch, setTextSearch] = useState('');
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [customerServiceOpenData, setCustomerServiceOpenData] = useState(null);
    const [displayedPeople, setDisplayedPeople] = useState(INITIAL_DISPLAY_COUNT);
    const [statusSession, setStatusSession] = useState('CLOSE');
    const [customMessage, setCustomMessage] = useState('');

    const listRef = useRef(null);

    // Memoized functions
    const showErrorToast = useCallback((message) => {
        toast.error(message, { position: "top-center", theme: "colored" });
    }, []);

    const showWarningToast = useCallback((message) => {
        toast.warn(message, { position: "top-center", theme: "colored" });
    }, []);

    const searchContacts = useCallback(async () => {
        setLoading(true);
        setLoadingContacts(true);
        try {
            const clientsData = await getFullClients();
            setContacts(clientsData);
        } catch (error) {
            showErrorToast('Erro ao carregar contatos');
        } finally {
            setLoading(false);
            setLoadingContacts(false);
        }
    }, [showErrorToast]);

    const verificationSession = useCallback((time) => {
        const isSessionClosed = !time || time === '0:00:00';
        setStatusSession(isSessionClosed ? 'CLOSE' : 'OPEN');
    }, []);

    const selectContact = useCallback(async (contact) => {
        setLoading(true);
        try {
            const sessionTime = await getTimeExpireSession(contact?.number_client);
            verificationSession(sessionTime);
            setContactSelected(contact);
        } catch (error) {
            showErrorToast('Erro ao verificar sessão do contato');
        } finally {
            setLoading(false);
        }
    }, [verificationSession, showErrorToast]);

    const msgCustomerServiceOpen = useCallback((data) => (
        <div>
            <p>Já existe atendimento em aberto!</p>
            <u onClick={() => setCustomerServiceOpenData(data)}>
                ver atendimento
            </u>
        </div>
    ), []);

    const handleSendMessage = useCallback(async () => {
        if (!contactSelected) {
            showWarningToast('Por favor escolha um contato!');
            return;
        }

        if (statusSession === 'OPEN' && customMessage.trim()) {
            setLoading(true);
            try {
                const { id, number_client } = contactSelected;
                const newCustomer = await postCustomer(id, IdUser(), false);

                if (!newCustomer?.id) {
                    showErrorToast(newCustomer?.message || 'Erro ao criar atendimento');
                    return;
                }

                const statusSend = await sendMessage(number_client, 'text', customMessage.trim(), newCustomer.id);
                if (!statusSend) {
                    showErrorToast('Não foi possível enviar a mensagem');
                    return;
                }

                SelectOrderList(1);
                handleMyChats();
                close();
            } catch (err) {
                showErrorToast('Erro ao enviar mensagem personalizada');
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!template) {
            showWarningToast('Por favor escolha uma mensagem!');
            return;
        }

        setLoading(true);
        try {
            const { id, number_client } = contactSelected;
            const customerOpen = await qtdCustomerServiceOpen(id, true);

            if (customerOpen.length > 0) {
                showWarningToast(msgCustomerServiceOpen(customerOpen));
                return;
            }

            const isTemplateTextParams = !template.text;
            const isSessionClosedOrHasButtons = statusSession === 'CLOSE' ||
                template?.utiliza_botoes === true ||
                template?.template?.utiliza_botoes === true;

            if (isSessionClosedOrHasButtons) {
                const statusSend = isTemplateTextParams
                    ? await sendMsgTemplateParams(number_client, template?.template.id, template?.parameters)
                    : await sendMsgTemplate(number_client, template?.id);

                if (!statusSend.success) {
                    showErrorToast(statusSend?.message || 'Não foi possível enviar a mensagem para este número');
                    return;
                }
            } else {
                const newCustomer = await postCustomer(id, IdUser(), false);

                if (!newCustomer?.id) {
                    showErrorToast(newCustomer?.message || 'Não foi possível enviar a mensagem para este número');
                    return;
                }

                const textToSend = template.text || template.textFormatted;
                const statusSend = await sendMessage(number_client, 'text', textToSend, newCustomer.id);

                if (!statusSend) {
                    showErrorToast('Não foi possível enviar a mensagem para este número');
                    return;
                }

                const isDocumentType = template?.tipo === "document" || template?.template?.tipo === "document";

                if (isDocumentType) {
                    const statusSendDoc = isTemplateTextParams
                        ? await sendDocumentByLink(
                            number_client,
                            template?.parameters[0],
                            template?.parameters[0].substring(template?.parameters[0].lastIndexOf("/") + 1),
                            newCustomer?.id
                        )
                        : await sendDocumentByLink(
                            number_client,
                            template?.url,
                            template?.nome_arquivo,
                            newCustomer?.id
                        );

                    if (!statusSendDoc.success) {
                        showErrorToast(statusSendDoc?.message || 'Não foi possível enviar o documento');
                        return;
                    }
                }
            }

            SelectOrderList(1);
            handleMyChats();
            close();
        } catch (error) {
            showErrorToast('Erro inesperado ao enviar mensagem');
        } finally {
            setLoading(false);
        }
    }, [contactSelected, template, customMessage, statusSession, msgCustomerServiceOpen, showWarningToast, showErrorToast, SelectOrderList, handleMyChats, close]);

    const onFilterContacts = useCallback((item) => {
        if (textSearch.length < MIN_SEARCH_LENGTH) return true;

        const textSearchLower = textSearch.toLowerCase();
        return item?.name?.toLowerCase()?.includes(textSearchLower) ||
            item?.number_client?.toLowerCase()?.includes(textSearchLower);
    }, [textSearch]);

    const handleScroll = useCallback(() => {
        const element = listRef.current;
        if (element && element.scrollTop + element.clientHeight + 50 >= element.scrollHeight) {
            setDisplayedPeople(prevCount => prevCount + LOAD_MORE_COUNT);
        }
    }, []);

    const handleContactNewBack = useCallback((contact) => {
        selectContact(contact);
        setOpenNewChat(false);
    }, [selectContact]);

    const handleTemplateChange = useCallback((selectedTemplate) => {
        setTemplate(selectedTemplate);
        setRender(2);
    }, []);

    // Effects
    useEffect(() => {
        searchContacts();
    }, [searchContacts]);

    useEffect(() => {
        if (contactSelected) {
            setTemplate(null);
        }
    }, [contactSelected]);

    useEffect(() => {
        const element = listRef.current;
        if (element) {
            element.addEventListener('scroll', handleScroll);
            return () => element.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Memoized computed values
    const visibleData = useMemo(() => 
        contacts?.filter(onFilterContacts).slice(0, displayedPeople)
    , [contacts, onFilterContacts, displayedPeople]);

    const contactElements = useMemo(() => {
        return visibleData?.map((item) => (
            <li 
                className='card--Contact'
                key={item.id}
                onClick={() => selectContact(item)}
            >
                <span className='avatar--'>
                    <Avatar
                        name={item?.name}
                        width={'3.5rem'}
                        height={'3.5rem'}
                        fontSize={'1.5rem'}
                    />
                </span>
                <span className='info--'>
                    <span className='name'>
                        <span className='name--overflow'>
                            {item?.name}
                        </span>
                    </span>
                    <span className='description'>
                        {item?.number_client}
                    </span>
                </span>
            </li>
        ));
    }, [visibleData, selectContact]);

    const SkeletonRender = useMemo(() => {
        const skeletonArray = Array.from({ length: SKELETON_COUNT }, (_, i) => (
            <Skeleton type='contact' key={i} />
        ));
        return <div>{skeletonArray}</div>;
    }, []);

    const templateText = useMemo(() => {
        if (!template) return 'Selecione uma mensagem ...';
        return template?.text || template?.textFormatted;
    }, [template]);

    return (
        <>
            <Container loading={loading ? 1 : 0}>
                <Content>
                    {!contactSelected ? (
                        <>
                            <Search
                                className='search'
                                value={textSearch}
                                onChange={(e) => setTextSearch(e.target.value)}
                            />
                            <AddContact onClick={() => setOpenNewChat(true)}>
                                <span>
                                    <IoMdPersonAdd fontSize={`1.5rem`} />
                                </span>
                                <div>Novo Contato</div>
                            </AddContact>
                            <Contacts ref={listRef}>
                                {loadingContacts ? SkeletonRender : contactElements}
                            </Contacts>
                        </>
                    ) : (
                        <>
                            {render === 1 && (
                                <SelectMessage>
                                    <span 
                                        className='back--'
                                        onClick={() => setContactSelected(null)}
                                    >
                                        <BiChevronLeft className='icon--' />
                                        VOLTAR
                                    </span>

                                    <div className={`list--message ${customMessage.trim() ? 'disabled' : ''}`}
                                        style={{
                                            pointerEvents: customMessage.trim() ? 'none' : 'auto',
                                            opacity: customMessage.trim() ? 0.5 : 1,
                                        }}>
                                        <MessageTemplates
                                            className='message--'
                                            isTemplatesOpenSession={statusSession === 'CLOSE'}
                                            onChange={handleTemplateChange}
                                            disabled={!!customMessage.trim()}
                                        />
                                    </div>

                                    {statusSession === 'OPEN' && (
                                        <div className='custom-message'>
                                            <textarea
                                                placeholder='Digite sua mensagem'
                                                value={customMessage}
                                                onChange={(e) => setCustomMessage(e.target.value)}
                                            />
                                            <Send loading={loading ? 1 : 0}>
                                                <button onClick={handleSendMessage}>
                                                    {!loading ? (
                                                        <>
                                                            ENVIAR <IoSendSharp />
                                                        </>
                                                    ) : (
                                                        <LoadingSpinner />
                                                    )}
                                                </button>
                                            </Send>
                                        </div>
                                    )}
                                </SelectMessage>
                            )}
                            {render === 2 && (
                                <Revision>
                                    <div 
                                        className='back--'
                                        onClick={() => setRender(1)}
                                    >
                                        <BiChevronLeft className='icon--' />
                                        VOLTAR
                                    </div>
                                    <label className='to--'>Enviar para:</label>
                                    <div className='contact--'>
                                        <div className='card--Contact contact--selected'>
                                            <span className='avatar--'>
                                                <Avatar
                                                    name={contactSelected?.name}
                                                    width={'3.5rem'}
                                                    height={'3.5rem'}
                                                    fontSize={'1.5rem'}
                                                />
                                            </span>
                                            <span className='info-- ste'>
                                                <span className='name'>
                                                    <span className='name--overflow'>
                                                        {contactSelected?.name}
                                                    </span>
                                                </span>
                                                <span className='description'>
                                                    {contactSelected?.number_client}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                    <label className='to--'>Mensagem:</label>
                                    <div className='message--selected'>
                                        <p>{templateText}</p>
                                    </div>
                                    <Send loading={loading ? 1 : 0}>
                                        <button onClick={handleSendMessage}>
                                            {!loading ? (
                                                <>
                                                    ENVIAR
                                                    <IoSendSharp />
                                                </>
                                            ) : (
                                                <LoadingSpinner />
                                            )}
                                        </button>
                                    </Send>
                                </Revision>
                            )}
                        </>
                    )}
                </Content>
            </Container>
            
            {openNewChat && (
                <ContactNew
                    onBack={handleContactNewBack}
                    onLoadContacts={searchContacts}
                />
            )}
            
            <Modal 
                title={'Atendimento em aberto'}
                isOpen={!!customerServiceOpenData}
                close={() => setCustomerServiceOpenData(null)}
            >
                <ViewerCustomerServiceOpen
                    customerServiceOpen={customerServiceOpenData}
                    onClose={() => setCustomerServiceOpenData(null)}
                />
            </Modal>
        </>
    );
}
